// Consent requests (spec 2026-09-26) in words: one place for how a status,
// a directory and a date read on every page that shows a request.

// Pill tone (sso-workspace .sw-pill) and label per request status.
const STATUS = {
  pending: ['warn', 'Waiting for approval'],
  approved: ['ok', 'Approved'],
  expired: ['bad', 'Expired'],
  cancelled: [null, 'Cancelled'],
  superseded: [null, 'Replaced']
}

export const consentStatusTone = status => (STATUS[status] || [null])[0]
export const consentStatusLabel = status => (STATUS[status] || [null, status])[1]

export const CONSENT_STATUS_FILTERS = [
  { value: '', label: 'All requests' },
  { value: 'pending', label: 'Waiting for approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'superseded', label: 'Replaced' }
]

// A request is still actionable (resend, copy, cancel) only while pending
// and inside its 7 days; the server reports a past-due pending row as
// expired already, this is the belt to that.
export const isOpen = (row, now = Date.now()) => row.status === 'pending' && new Date(row.expiresAt).getTime() > now

export const shortTenant = tenantID => (tenantID ? `${tenantID.slice(0, 8)}…` : '')

// How a request names its directory: the domain the requester typed, else
// the short tenant id.
export const directoryLabel = row => (row.tenantDomain ? row.tenantDomain : shortTenant(row.tenantID))

export const formatDate = value =>
  value ? new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : ''

export const formatDateTime = value =>
  value ? new Date(value).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const DOMAIN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i
const EMAIL = /^[^\s@,<>]+@[^\s@,<>]+\.[^\s@,<>]+$/

// The "School domain or tenant ID" field accepts a GUID, a domain, or the
// shapes people paste (an address, a URL) -- the server normalises them the
// same way; this only decides whether to send at all.
export const tenantInputProblem = raw => {
  const value = (raw || '').trim()
  if (!value) return "Enter the organization's domain (for example example.org) or its tenant ID."
  if (GUID.test(value)) return null
  const host = value.toLowerCase().replace(/^.*@/, '').replace(/^https?:\/\//, '').replace(/[/?#].*$/, '').replace(/\.$/, '')
  return DOMAIN.test(host) ? null : `"${value}" isn't a domain or a tenant ID.`
}

export const emailProblem = raw => {
  const value = (raw || '').trim()
  if (!value) return null
  return EMAIL.test(value) ? null : 'Enter a valid email address.'
}

// The server's own message when it sent one; every consent endpoint writes
// its refusals for the person reading the form.
export const serverMessage = (err, fallback) => (err && err.data && err.data.message) || fallback

// Copying lives in shared/clipboard/clipboard.js: one helper for every Copy
// button, written so Safari allows it.
